// @flow
import React from 'react';
import { FormattedMessage, intlShape } from 'react-intl';
import { Button, buttonTypes } from 'moods-react/Button';

import { SelectDropdown } from '../../../../components/SelectDropdown';
import { AutocompleteDropdown } from '../../../../components/AutocompleteDropdown';
import RoundButton from '../../../common/round-button/RoundButtonComponent';
import CloseButton from '../../../common/close-button/CloseButtonComponent';
import TickSuccessIcon from '../../../common/icons/tick-success/TickSuccessComponent';

import { getProductSizes } from '../../../services/product-type/ProductTypeService';
import messages from '../../../../data/products';
import productTypes from '../../../../data/product-data';
import { getProductRouteById } from '../../../services/products-route/ProductsRouteService';
import { envUrl } from '../../../services/environment/EnvironmentService';

import './FilterBar.scss';

type Props = {
  productTypeId: string,
  productSize: string,
  productSort: string,
  template: string,
  isVisible?: boolean,
  onCloseFilters?: Function,
  productTemplates?: [],
};

const sortOptions = [
  {
    labelKey: 'sortByNewestFirst',
    value: 'updatedDesc',
  },
  {
    labelKey: 'sortByNameAlphabetical',
    value: 'nameAsc',
  },
  {
    labelKey: 'sortByOldestFirst',
    value: 'updatedAsc',
  },
];

/**
 * Displays Filter Bar
 */
class FilterBarComponent extends React.Component {
  constructor(props: Props) {
    super(props);
    this.state = {
      templateFilter: this.props.template,
      sortFilter: this.props.productSort,
      sizeFilter: this.props.productSize,
    };
  }

  /* eslint complexity: 0  */
  shouldComponentUpdate(
    newProps: Props,
    newState: { templateFilter: string, sizeFilter: string, sortFilter: string },
  ): boolean {
    return (
      newProps.productTemplates !== this.props.productTemplates ||
      newProps.productTypeId !== this.props.productTypeId ||
      newProps.isVisible !== this.props.isVisible ||
      newProps.template !== this.props.template ||
      newProps.productSize !== this.props.productSize ||
      newProps.productSort !== this.props.productSort ||
      newState.templateFilter !== this.state.templateFilter ||
      newState.sizeFilter !== this.state.sizeFilter ||
      newState.sortFilter !== this.state.sortFilter
    );
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props): void {
    // Restore current state just after the bar is shown
    if (nextProps.isVisible && !this.props.isVisible) {
      this.resetState();
    }
  }

  resetState(): void {
    this.setState({
      templateFilter: this.props.template,
      sortFilter: this.props.productSort,
      sizeFilter: this.props.productSize,
    });
  }

  onTemplateSelected = (template: { value: string }): void => {
    const selectedTemplate = template === null ? 'ALL' : template.value; // This can be null as the field is clearable

    this.updateStateFilter('templateFilter', selectedTemplate);
  };

  onSizeSelected = (value: string): void => {
    this.updateStateFilter('sizeFilter', value);
  };

  onSortChanged = (value: string): void => {
    this.updateStateFilter('sortFilter', value);
  };

  onFiltersApplied = (): void => {
    if (this.hasActiveFilters()) {
      this.props.onCloseFilters(this.state);
    }
  };

  closeFilters = (): void => {
    this.props.onCloseFilters(null);
    if (!this.unfiltered()) {
      this.resetAllFilters();
    }
  };

  resetAllFilters = (): void => {
    this.props.history.push({
      pathname: envUrl(`/products/${getProductRouteById(this.props.productTypeId)}`),
      search: `?productSize=ALL&productSort=${this.props.productSort}&template=ALL&searchTerm=`,
    });
  };

  updateStateFilter(filterName: string, selectedFilterValue: string): void {
    const currentFilterValue = this.state[filterName];
    if (currentFilterValue !== selectedFilterValue) {
      this.setState({
        [filterName]: selectedFilterValue,
      });
    }
  }

  buildTemplateOptions(): Array<{ value: string, label: string }> {
    const currentOptions = this.props.productTemplates.map((data) => ({
      value: data.id,
      label: data.name,
    }));

    const allItemsOption = {
      value: 'ALL',
      label: this.context.intl.formatMessage(messages.allTemplatesFilter), // All templates
    };
    currentOptions.unshift(allItemsOption);

    return currentOptions;
  }

  buildSizeOptions(): Array<[string, string]> {
    const productType = this.props.productTypeId;
    const sizes = getProductSizes(productType);
    const currentOptions = sizes.map((sizeItem) => [
      sizeItem.id,
      this.context.intl.formatMessage(productTypes[`productSize_${sizeItem.id}`]),
    ]);

    const allItemsOption = [
      'ALL',
      this.context.intl.formatMessage(messages.allSizesFilter), // All sizes
    ];
    currentOptions.unshift(allItemsOption);

    return currentOptions;
  }

  buildSortOptions(): Array<[string, string]> {
    return sortOptions.map((option) => [
      option.value,
      this.context.intl.formatMessage(messages[option.labelKey]), // Sorting type
    ]);
  }

  unfiltered(): boolean {
    return this.props.template === 'ALL' && this.props.productSize === 'ALL';
  }

  hasActiveFilters(): boolean {
    return (
      this.props.template !== this.state.templateFilter ||
      this.props.productSize !== this.state.sizeFilter ||
      this.props.productSort !== this.state.sortFilter
    );
  }

  render(): Element {
    const visibleClassName = this.props.isVisible ? ' toolbar-sliding--visible' : '';
    const sizeOptions = this.buildSizeOptions();
    const templateOptions = this.buildTemplateOptions();
    const localisedSortOptions = this.buildSortOptions();
    const allTemplatesMessage = this.context.intl.formatMessage(messages.allTemplatesFilter); // All templates
    const clearFiltersMessage = this.context.intl.formatMessage(messages.clearAllFiltersButton); // Clear all
    const applyFiltersMessage = this.context.intl.formatMessage(messages.applyFiltersButton); // Apply

    const templateValue = this.state.templateFilter
      ? templateOptions.find(({ value }) => value === this.state.templateFilter)
      : templateOptions[0];

    return (
      <div className={`toolbar-sliding${visibleClassName} filter-bar-component`}>
        <span className="filter-bar-component__label">
          <FormattedMessage id="app.searchBar.filter.label.filterBy" defaultMessage="Filter by" />
        </span>
        <div className="filter-bar-component__filter">
          <AutocompleteDropdown
            width={254}
            placeholder={allTemplatesMessage}
            values={templateOptions}
            value={templateValue}
            onChange={this.onTemplateSelected}
            noOptionsText="No results found"
          />
        </div>
        <div className="filter-bar-component__filter">
          <SelectDropdown
            values={sizeOptions}
            data-sizesfilter
            defaultValue={this.state.sizeFilter}
            onChange={this.onSizeSelected}
          />
        </div>

        <span className="filter-bar-component__label">
          <FormattedMessage id="app.searchBar.filter.label.sortBy" defaultMessage="Sort by" />
        </span>
        <div className="filter-bar-component__filter">
          <SelectDropdown
            values={localisedSortOptions}
            defaultValue={this.state.sortFilter}
            onChange={this.onSortChanged}
          />
        </div>

        <div
          className="filter-bar-component__action filter-bar-component__action--apply-button"
          onClick={this.onFiltersApplied}
        >
          <Button onClick={() => {}} disabled={!this.hasActiveFilters()}>
            {applyFiltersMessage}
          </Button>
        </div>

        <div
          className="filter-bar-component__action filter-bar-component__action--apply-tick"
          onClick={this.onFiltersApplied}
        >
          <RoundButton size="small" disabled={!this.hasActiveFilters()} type="primary" icon={<TickSuccessIcon />} />
        </div>

        <span className="filter-bar-component__action filter-bar-component__close-button" onClick={this.closeFilters}>
          {this.unfiltered() ? (
            <CloseButton />
          ) : (
            <Button onClick={() => {}} type={buttonTypes.SECONDARY}>
              {clearFiltersMessage}
            </Button>
          )}
        </span>
      </div>
    );
  }
}

FilterBarComponent.displayName = 'FilterBar';

FilterBarComponent.defaultProps = {
  isVisible: false,
  onCloseFilters: () => null,
  productTemplates: [],
};

FilterBarComponent.contextTypes = {
  intl: intlShape.isRequired,
};

export default FilterBarComponent;
